import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import type { SimulationRendererProps, RosterOrganism, BodyTypeSchema, GeomDef, MeshGeomDef, MeshData } from '../types/simulation';

const MAX_FIXTURES = 4096;
const MAX_SITES    = 512;
const RECORD_BYTES = 43;  // 23 base + 16 quaternion + 4 geom_scale
const _dummy = new THREE.Object3D();
const _color = new THREE.Color();
new THREE.Vector3();
const _fwd   = new THREE.Vector3();

// Coordinate transform: sim(x,y,z) → Three.js(x,z,-y)  =  rotation -90° around X
// Used to convert both positions and quaternions from sim space to Three.js space.
const COORD_QUAT     = new THREE.Quaternion(-Math.SQRT1_2, 0, 0, Math.SQRT1_2); // R_x(-90°)
const COORD_QUAT_INV = new THREE.Quaternion( Math.SQRT1_2, 0, 0, Math.SQRT1_2); // R_x(+90°)

function simQuatToThree(qw: number, qx: number, qy: number, qz: number): THREE.Quaternion {
  const q = new THREE.Quaternion(qx, qy, qz, qw); // THREE uses (x,y,z,w)
  return COORD_QUAT.clone().multiply(q).multiply(COORD_QUAT_INV);
}

function b64ToFloat32(b64: string): Float32Array {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Float32Array(buf.buffer);
}

function b64ToInt32(b64: string): Int32Array {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Int32Array(buf.buffer);
}

// Build a Three.js BufferGeometry from a MuJoCo geom definition.
// Geometry is defined in the body's Three.js local frame (after coord transform).
// Axes after coord transform: local-x=sim-x, local-y=sim-z, local-z=-sim-y
function makeGeomGeometry(geom: GeomDef, meshes?: Record<string, MeshData>): THREE.BufferGeometry {
  switch (geom.type) {
    case 'sphere': {
      const [s0] = geom.size;
      return new THREE.SphereGeometry(s0, 12, 8);
    }
    case 'capsule': {
      // MuJoCo capsule: axis along body-local z → Three.js local y after coord transform
      // size[0]=radius, size[1]=half-length of cylindrical part
      const [s0, s1] = geom.size;
      return new THREE.CapsuleGeometry(s0, s1 * 2, 4, 12);
    }
    case 'cylinder': {
      // Same axis mapping as capsule
      const [s0, s1] = geom.size;
      return new THREE.CylinderGeometry(s0, s0, s1 * 2, 12);
    }
    case 'box': {
      // size[0,1,2] = half-extents along sim x,y,z
      // → Three.js local: x=s0, y=s2, z=s1 (after coord transform)
      const [s0, s1, s2] = geom.size;
      return new THREE.BoxGeometry(s0 * 2, s2 * 2, s1 * 2);
    }
    case 'ellipsoid':
      return new THREE.SphereGeometry(1, 12, 8);
    case 'mesh': {
      const g = geom as MeshGeomDef;
      const meshData = meshes?.[g.mesh_key];
      if (!meshData) return new THREE.SphereGeometry(0.05, 6, 4); // fallback if schema incomplete
      const verts = b64ToFloat32(meshData.verts_b64);
      const faces  = b64ToInt32(meshData.faces_b64);
      // Transform vertices from sim Z-up (x,y,z) → Three.js Y-up (x,z,-y)
      for (let i = 0; i < verts.length; i += 3) {
        const vy = verts[i + 1], vz = verts[i + 2];
        verts[i + 1] = vz;
        verts[i + 2] = -vy;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
      geo.setIndex(new THREE.BufferAttribute(new Uint32Array(faces.buffer), 1));
      geo.computeVertexNormals();
      return geo;
    }
    default:
      return new THREE.SphereGeometry(0.05, 6, 4);
  }
}

function makeGeomMesh(geom: GeomDef, meshes?: Record<string, MeshData>): THREE.Mesh {
  const geo = makeGeomGeometry(geom, meshes);
  const [, , , a] = geom.rgba;
  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.90, 0.48, 0.15),
    roughness: 0.65,
    metalness: 0.05,
    transparent: a < 0.99,
    opacity: a,
  });
  const mesh = new THREE.Mesh(geo, mat);

  // Local position: apply coord transform to geom_pos (sim body-local)
  const [px, py, pz] = geom.pos;
  mesh.position.set(px, pz, -py);

  // Local orientation: apply coord transform to geom_quat
  const [qw, qx, qy, qz] = geom.quat;
  mesh.quaternion.copy(simQuatToThree(qw, qx, qy, qz));

  // Ellipsoid: scale the unit sphere by semi-axes
  if (geom.type === 'ellipsoid') {
    const [s0, s1, s2] = geom.size;
    mesh.scale.set(s0, s2, s1); // sim-x→three-x, sim-z→three-y, sim-y→three-z
  }

  return mesh;
}

// Build one Three.js Group for a single body segment, containing all its geom meshes.
function buildSegmentGroup(segment: { name?: string; geoms: GeomDef[] }, meshes?: Record<string, MeshData>): THREE.Group {
  const group = new THREE.Group();
  for (const geom of segment.geoms) {
    if (geom.type === 'mesh') {
      const key = (geom as MeshGeomDef).mesh_key;
      const md = meshes?.[key];
      console.log(`[sim] mesh geom key=${key} found=${!!md} verts=${md ? b64ToFloat32(md.verts_b64).length / 3 : 0}`);
    }
    group.add(makeGeomMesh(geom, meshes));
  }
  return group;
}

export default function DefaultSimRenderer({
  broadcastUrl,
  config,
  selectedEntityId,
  hoveredEntityId,
  snapToEntityId,
  onOrganismClick,
  onOrganismHover,
}: SimulationRendererProps) {
  const [broadcastOffline, setBroadcastOffline] = useState(false);
  const [webglUnavailable, setWebglUnavailable] = useState(false);

  const mountRef   = useRef<HTMLDivElement>(null);
  const tickDisplayRef = useRef<HTMLSpanElement>(null);
  const tickRateRef    = useRef<HTMLSpanElement>(null);
  const wsRef      = useRef<WebSocket | null>(null);
  const rosterRef  = useRef<RosterOrganism[]>([]);
  const siteRegistryRef = useRef<string[]>([]);
  const latestBufRef = useRef<{ buf: ArrayBuffer; view: DataView } | null>(null);
  const instanceIdToOrgIdxRef = useRef(new Map<number, number>());

  // MJCF body schema state
  const bodySchemaRef    = useRef<Record<string, BodyTypeSchema>>({});
  // entity_id → array of Three.js Groups (one per fixture slot / body segment)
  const entityGroupsRef  = useRef<Map<string, THREE.Group[]>>(new Map());
  // fixture slot → { entityId, segIdx } for schema-rendered slots
  const slotToEntityRef  = useRef<Map<number, { entityId: string; segIdx: number }>>(new Map());
  // Reference to the scene for adding/removing groups (set during scene setup)
  const sceneRef         = useRef<THREE.Scene | null>(null);
  const fixturesRef      = useRef<THREE.InstancedMesh | null>(null); // sphere fallback

  const selectedEntityIdRef = useRef(selectedEntityId);
  const hoveredEntityIdRef  = useRef(hoveredEntityId);
  const snapToEntityIdRef   = useRef(snapToEntityId);
  const onOrganismClickRef  = useRef(onOrganismClick);
  const onOrganismHoverRef  = useRef(onOrganismHover);
  const entityPositionsRef  = useRef<Map<string, THREE.Vector3>>(new Map());
  const raycasterTargetsRef = useRef<THREE.Mesh[]>([]);

  useEffect(() => { selectedEntityIdRef.current = selectedEntityId; }, [selectedEntityId]);
  useEffect(() => { hoveredEntityIdRef.current  = hoveredEntityId;  }, [hoveredEntityId]);
  useEffect(() => { snapToEntityIdRef.current   = snapToEntityId;   }, [snapToEntityId]);
  useEffect(() => { onOrganismClickRef.current  = onOrganismClick;  }, [onOrganismClick]);
  useEffect(() => { onOrganismHoverRef.current  = onOrganismHover;  }, [onOrganismHover]);

  // Rebuild entity groups whenever roster or schemas change.
  const rebuildEntityGroups = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const roster = rosterRef.current;
    const schemas = bodySchemaRef.current;
    const entityGroups = entityGroupsRef.current;
    const slotToEntity = slotToEntityRef.current;

    // Remove groups for entities no longer in roster (or dead)
    const rosterIds = new Set(roster.filter(r => r.state !== 'dead').map(r => r.entity_id));
    for (const [entityId, groups] of entityGroups) {
      if (!rosterIds.has(entityId)) {
        for (const g of groups) scene.remove(g);
        entityGroups.delete(entityId);
        // Clean up slot mapping
        for (const [slot, info] of slotToEntity) {
          if (info.entityId === entityId) slotToEntity.delete(slot);
        }
      }
    }

    // Create groups for new entities that have a schema
    for (const org of roster.filter(r => r.state !== 'dead')) {
      if (entityGroups.has(org.entity_id)) continue;
      const bodyType = org.body_type;
      if (!bodyType || !schemas[bodyType]) continue;

      const schema = schemas[bodyType];
      const groups: THREE.Group[] = [];
      for (let segIdx = 0; segIdx < schema.bodies.length && segIdx < org.fixture_slots.length; segIdx++) {
        const group = buildSegmentGroup(schema.bodies[segIdx], schema.meshes);
        group.traverse(obj => { if (obj instanceof THREE.Mesh) obj.userData.entityId = org.entity_id; });
        scene.add(group);
        groups.push(group);
        slotToEntity.set(org.fixture_slots[segIdx], { entityId: org.entity_id, segIdx });
      }
      if (groups.length > 0) entityGroups.set(org.entity_id, groups);
    }

    // Rebuild flat mesh list for raycasting
    const targets: THREE.Mesh[] = [];
    for (const groups of entityGroups.values()) {
      for (const g of groups) {
        g.traverse(obj => { if (obj instanceof THREE.Mesh) targets.push(obj); });
      }
    }
    raycasterTargetsRef.current = targets;
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    const foodItems = config?.world?.static_objects?.food;
    if (!scene || !foodItems?.length) return;

    const geo = new THREE.SphereGeometry(0.4, 8, 6);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x5aaa3a,
      emissive: new THREE.Color(0.2, 0.5, 0.05),
      emissiveIntensity: 1.2,
      roughness: 0.7,
      metalness: 0.0,
    });
    const meshes = foodItems.map(item => {
      const [fx, fy, fz = 0] = item.position;
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(fx, fz, -fy); // sim(x,y,z) → three(x,z,-y)
      scene.add(mesh);
      return mesh;
    });

    return () => {
      for (const m of meshes) scene.remove(m);
      geo.dispose();
      mat.dispose();
    };
  }, [config]);

  const connectBroadcast = useCallback(() => {
    wsRef.current?.close();
    const ws = new WebSocket(broadcastUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      if (typeof evt.data === 'string') {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'roster') {
            rosterRef.current = msg.organisms ?? [];
            console.log('[sim] roster', msg.organisms);
            rebuildEntityGroups();
          } else if (msg.type === 'body_schema') {
            bodySchemaRef.current = msg.schemas ?? {};
            const schemas = msg.schemas ?? {};
            for (const [bt, s] of Object.entries(schemas) as [string, any][]) {
              const meshCount = Object.keys(s.meshes ?? {}).length;
              const geomCounts = (s.bodies ?? []).map((b: any) => `${b.name}:${(b.geoms ?? []).length}geoms`);
              console.log(`[sim] body_schema ${bt}: ${meshCount} meshes, bodies=[${geomCounts.join(', ')}]`);
            }
            rebuildEntityGroups();
          } else if (msg.type === 'site_registry') {
            siteRegistryRef.current = msg.sites ?? [];
          } else if (msg.type === 'stats' && tickRateRef.current) {
            const hz = msg.actual_tick_rate as number;
            tickRateRef.current.textContent = hz > 0 ? hz.toFixed(1) : '—';
          }
        } catch { /* ignore malformed */ }
        return;
      }
      const buf = evt.data as ArrayBuffer;
      if (buf.byteLength < 16) return;
      latestBufRef.current = { buf, view: new DataView(buf) };
    };

    ws.onerror = () => setBroadcastOffline(true);
    ws.onclose = () => { wsRef.current = null; };
  }, [broadcastUrl, rebuildEntityGroups]);

  useEffect(() => {
    setBroadcastOffline(false);
    connectBroadcast();
    return () => { wsRef.current?.close(); wsRef.current = null; };
  }, [connectBroadcast]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ── Renderer ────────────────────────────────────────────────────────────
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch {
      setWebglUnavailable(true);
      return;
    }
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.domElement.style.touchAction = 'none';
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.3;
    mount.appendChild(renderer.domElement);

    // ── Scene ───────────────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x061828);
    scene.fog = new THREE.FogExp2(0x071c30, 0.005);
    sceneRef.current = scene;

    // ── Camera ──────────────────────────────────────────────────────────────
    // Coordinate mapping: sim(x, y, z) → Three.js(x, z, -y)
    const camera = new THREE.PerspectiveCamera(75, mount.clientWidth / mount.clientHeight, 0.1, 2000);
    camera.position.set(64, 50, 30);
    camera.lookAt(64, 0, -64);

    // ── Lighting — aquarium ───────────────────────────────────────────────────
    scene.add(new THREE.AmbientLight(0x153050, 1.4));             // deep blue ambient
    const sunLight = new THREE.DirectionalLight(0xb8d8f8, 2.2);  // sunlight through water
    sunLight.position.set(0, 300, 50);
    scene.add(sunLight);
    const waterGlow = new THREE.PointLight(0x1a4878, 0.9, 600);  // deep water scatter
    waterGlow.position.set(64, 6, -64);
    scene.add(waterGlow);

    // ── Floor grid ───────────────────────────────────────────────────────────
    const gridHelper = new THREE.GridHelper(512, 64, 0x2a1e0e, 0x180e06);
    gridHelper.position.set(64, 0, -64);
    scene.add(gridHelper);

    // ── Camera rotation (drag to look) ──────────────────────────────────────
    camera.rotation.order = 'YXZ';
    let yaw   = camera.rotation.y;
    let pitch = camera.rotation.x;
    let prevHoveredFromCanvas: string | undefined;
    const mousePosNDC = new THREE.Vector2(0, 0);

    // ── Sphere fallback InstancedMesh (for fixtures without body schema) ─────
    const fixGeo = new THREE.SphereGeometry(1, 8, 6);
    const fixMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(1.0, 1.0, 1.0),
      roughness: 0.65,
      metalness: 0.05,
      vertexColors: true,
    });
    const fixtures = new THREE.InstancedMesh(fixGeo, fixMat, MAX_FIXTURES);
    fixtures.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    fixtures.frustumCulled = false;
    fixtures.count = 0;
    scene.add(fixtures);
    fixturesRef.current = fixtures;

    // ── Site points ──────────────────────────────────────────────────────────
    const sitePosArr = new Float32Array(MAX_SITES * 3);
    const siteColArr = new Float32Array(MAX_SITES * 3);
    const siteGeo = new THREE.BufferGeometry();
    siteGeo.setAttribute('position', new THREE.BufferAttribute(sitePosArr, 3));
    siteGeo.setAttribute('color',    new THREE.BufferAttribute(siteColArr, 3));
    siteGeo.setDrawRange(0, 0);
    scene.add(new THREE.Points(siteGeo, new THREE.PointsMaterial({
      size: 5, sizeAttenuation: false, vertexColors: true,
    })));

    // ── Input ────────────────────────────────────────────────────────────────
    const raycaster = new THREE.Raycaster();
    let prevHighlightedId: string | undefined;

    // Drag-to-rotate state
    let dragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let lastX = 0;
    let lastY = 0;
    const DRAG_THRESHOLD = 5;
    const activePointers = new Map<number, { x: number; y: number }>();
    let pinching = false;
    let lastPinchDist = 0;
    const getPinchDist = () => {
      const pts = [...activePointers.values()];
      return Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
    };
    const LOOK_SENSITIVITY = 0.003;
    const CLICK_PICK_PX = 30;
    const HOVER_PICK_PX = 20;
    const _pickVec = new THREE.Vector3();

    const findClosestEntityOnScreen = (px: number, py: number, rect: DOMRect, threshold: number): string | undefined => {
      let best: string | undefined;
      let bestDist = threshold;
      for (const [eid, pos] of entityPositionsRef.current) {
        _pickVec.copy(pos).project(camera);
        if (_pickVec.z > 1) continue; // behind camera
        const sx = (_pickVec.x + 1) / 2 * rect.width;
        const sy = (-_pickVec.y + 1) / 2 * rect.height;
        const d = Math.hypot(sx - px, sy - py);
        if (d < bestDist) { bestDist = d; best = eid; }
      }
      return best;
    };

    const onPointerDown = (e: PointerEvent) => {
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      renderer.domElement.setPointerCapture(e.pointerId);
      if (activePointers.size === 2) {
        dragging = false;
        pinching = true;
        lastPinchDist = getPinchDist();
      } else if (activePointers.size === 1) {
        dragging = true;
        pinching = false;
        dragStartX = e.clientX; dragStartY = e.clientY;
        lastX = e.clientX;      lastY = e.clientY;
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (activePointers.size === 2) {
        const dist = getPinchDist();
        const delta = lastPinchDist - dist;
        lastPinchDist = dist;
        camera.fov = Math.max(10, Math.min(120, camera.fov + delta * 0.1));
        camera.updateProjectionMatrix();
        return;
      }
      const rect = renderer.domElement.getBoundingClientRect();
      mousePosNDC.set(
        ((e.clientX - rect.left) / rect.width)  *  2 - 1,
        ((e.clientY - rect.top)  / rect.height) * -2 + 1,
      );
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      yaw   -= dx * LOOK_SENSITIVITY;
      pitch -= dy * LOOK_SENSITIVITY;
      pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, pitch));
      camera.rotation.set(pitch, yaw, 0, 'YXZ');
    };

    const onPointerUp = (e: PointerEvent) => {
      activePointers.delete(e.pointerId);
      if (activePointers.size > 0) { dragging = false; return; }
      const wasDrag = pinching || Math.hypot(e.clientX - dragStartX, e.clientY - dragStartY) > DRAG_THRESHOLD;
      dragging = false;
      pinching = false;
      if (wasDrag) return;

      // Treat as click: raycast from pointer position
      const rect = renderer.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width)  *  2 - 1,
        ((e.clientY - rect.top)  / rect.height) * -2 + 1,
      );
      raycaster.setFromCamera(ndc, camera);

      const schemaTargets = raycasterTargetsRef.current;
      let clickedId: string | undefined;
      if (schemaTargets.length > 0) {
        const hits = raycaster.intersectObjects(schemaTargets, false);
        if (hits.length > 0) clickedId = (hits[0].object as THREE.Mesh).userData.entityId;
      }
      if (clickedId === undefined) {
        const hits = raycaster.intersectObject(fixtures);
        const instanceId = hits.length > 0 ? hits[0].instanceId : undefined;
        const orgIdx = instanceId !== undefined ? (instanceIdToOrgIdxRef.current.get(instanceId) ?? -1) : -1;
        if (orgIdx !== -1) clickedId = rosterRef.current.find(r => r.idx === orgIdx)?.entity_id;
      }
      if (clickedId === undefined) {
        clickedId = findClosestEntityOnScreen(e.clientX - rect.left, e.clientY - rect.top, rect, CLICK_PICK_PX);
      }
      onOrganismClickRef.current?.(
        clickedId
          ? (clickedId === selectedEntityIdRef.current ? undefined : clickedId)
          : undefined
      );
    };

    const onPointerLeave = () => { activePointers.clear(); dragging = false; pinching = false; };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      camera.fov = Math.max(10, Math.min(120, camera.fov + e.deltaY * 0.05));
      camera.updateProjectionMatrix();
    };

    renderer.domElement.addEventListener('pointerdown',  onPointerDown);
    renderer.domElement.addEventListener('pointermove',  onPointerMove);
    renderer.domElement.addEventListener('pointerup',    onPointerUp);
    renderer.domElement.addEventListener('pointerleave', onPointerLeave);
    renderer.domElement.addEventListener('wheel',        onWheel, { passive: false });

    // ── rAF loop ─────────────────────────────────────────────────────────────
    let animId: number;

    function animate() {
      animId = requestAnimationFrame(animate);

      // Update fixtures from latest broadcast frame
      const frame = latestBufRef.current;
      if (frame) {
        const { buf, view } = frame;
        const tick = view.getBigUint64(0, true);
        if (tickDisplayRef.current) tickDisplayRef.current.textContent = tick.toString();
        const fixture_count = view.getUint16(12, true);
        const site_count    = view.getUint16(14, true);
        let off = 16;
        let n = 0;  // sphere fallback instance counter
        instanceIdToOrgIdxRef.current.clear();

        const slotToEntity = slotToEntityRef.current;
        const entityGroups = entityGroupsRef.current;
        const seenPositions = new Set<string>();

        for (let i = 0; i < fixture_count; i++) {
          if (off + RECORD_BYTES > buf.byteLength) break;
          const slotIdx = view.getUint16(off,      true);
          const orgIdx  = view.getUint16(off + 2,  true);
          const x  = view.getFloat32(off + 4,  true);
          const y  = view.getFloat32(off + 8,  true);
          const z  = view.getFloat32(off + 12, true);
          const r  = view.getFloat32(off + 16, true);
          const qw        = view.getFloat32(off + 23, true);
          const qx        = view.getFloat32(off + 27, true);
          const qy        = view.getFloat32(off + 31, true);
          const qz        = view.getFloat32(off + 35, true);
          const geomScale = view.getFloat32(off + 39, true);
          off += RECORD_BYTES;

          // Track world position for each entity (first slot per entity wins)
          const eidForSlot = slotToEntity.get(slotIdx)?.entityId
            ?? rosterRef.current.find(r => r.idx === orgIdx)?.entity_id;
          if (eidForSlot && !seenPositions.has(eidForSlot)) {
            seenPositions.add(eidForSlot);
            let ep = entityPositionsRef.current.get(eidForSlot);
            if (!ep) { ep = new THREE.Vector3(); entityPositionsRef.current.set(eidForSlot, ep); }
            ep.set(x, z, -y);
          }

          // If this slot has a schema-rendered group, update its transform.
          const slotInfo = slotToEntity.get(slotIdx);
          if (slotInfo) {
            const groups = entityGroups.get(slotInfo.entityId);
            const group  = groups?.[slotInfo.segIdx];
            if (group) {
              if (slotInfo.segIdx === 0) {
                const q = simQuatToThree(qw, qx, qy, qz);
                console.log(`[sim] seg0 sim=(${x.toFixed(3)},${y.toFixed(3)},${z.toFixed(3)}) three_pos=(${x.toFixed(3)},${z.toFixed(3)},${(-y).toFixed(3)}) three_quat=(${q.x.toFixed(3)},${q.y.toFixed(3)},${q.z.toFixed(3)},${q.w.toFixed(3)})`);
              }
              group.position.set(x, z, -y);
              group.quaternion.copy(simQuatToThree(qw, qx, qy, qz));
              group.scale.setScalar(geomScale > 0 ? geomScale : 1);
            }
            continue; // don't also render a fallback sphere
          }

          // Fallback: render as a sphere instance — skip unowned/dead slots
          if (orgIdx === 0xFFFF) continue;
          const orgEntry = rosterRef.current.find(r => r.idx === orgIdx);
          if (!orgEntry || orgEntry.state === 'dead') continue;
          if (n >= MAX_FIXTURES) continue;
          _dummy.position.set(x, z, -y);
          _dummy.scale.setScalar(Math.max(0.5, r));
          _dummy.updateMatrix();
          fixtures.setMatrixAt(n, _dummy.matrix);
          fixtures.setColorAt(n, _color.setRGB(0.90, 0.48, 0.15));
          instanceIdToOrgIdxRef.current.set(n, orgIdx);
          n++;
        }

        fixtures.count = n;
        fixtures.instanceMatrix.needsUpdate = true;
        if (fixtures.instanceColor) fixtures.instanceColor.needsUpdate = true;

        // Parse site records from binary (follow all fixture records)
        const registry = siteRegistryRef.current;
        let si = 0;
        for (let i = 0; i < site_count; i++) {
          if (off + 14 > buf.byteLength) break;
          const siteId = view.getUint16(off,      true);
          const sx     = view.getFloat32(off + 2,  true);
          const sy     = view.getFloat32(off + 6,  true);
          const sz     = view.getFloat32(off + 10, true);
          off += 14;
          if (si >= MAX_SITES) continue;
          sitePosArr[si * 3]     = sx;
          sitePosArr[si * 3 + 1] = sz;
          sitePosArr[si * 3 + 2] = -sy;
          const name = registry[siteId] ?? '';
          const isMouth = name.endsWith('_mouth');
          siteColArr[si * 3]     = isMouth ? 0.98 : 0.38;
          siteColArr[si * 3 + 1] = isMouth ? 0.52 : 0.80;
          siteColArr[si * 3 + 2] = isMouth ? 0.18 : 0.98;
          si++;
        }
        siteGeo.setDrawRange(0, si);
        if (si > 0) {
          (siteGeo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
          (siteGeo.attributes.color    as THREE.BufferAttribute).needsUpdate = true;
        }
      }

      // Hover highlight: swap material color (MeshBasicMaterial has no emissive)
      const NEON_BASE    = new THREE.Color(0.90, 0.48, 0.15);
      const NEON_HOVER   = new THREE.Color(1.0, 0.82, 0.40);
      const currentHighlight = hoveredEntityIdRef.current;
      if (currentHighlight !== prevHighlightedId) {
        if (prevHighlightedId !== undefined) {
          const prevGroups = entityGroupsRef.current.get(prevHighlightedId);
          if (prevGroups) {
            for (const g of prevGroups) {
              g.traverse(obj => {
                if (obj instanceof THREE.Mesh) (obj.material as THREE.MeshStandardMaterial).color.copy(NEON_BASE);
              });
            }
          }
        }
        if (currentHighlight !== undefined) {
          const newGroups = entityGroupsRef.current.get(currentHighlight);
          if (newGroups) {
            for (const g of newGroups) {
              g.traverse(obj => {
                if (obj instanceof THREE.Mesh) (obj.material as THREE.MeshStandardMaterial).color.copy(NEON_HOVER);
              });
            }
          }
        }
        prevHighlightedId = currentHighlight;
      }

      // Hover: raycast from mouse position (schema meshes + sphere fallback)
      if (!dragging) {
        raycaster.setFromCamera(mousePosNDC, camera);
        let canvasHoveredId: string | undefined;

        const schemaTargets = raycasterTargetsRef.current;
        if (schemaTargets.length > 0) {
          const schemaHits = raycaster.intersectObjects(schemaTargets, false);
          if (schemaHits.length > 0) canvasHoveredId = (schemaHits[0].object as THREE.Mesh).userData.entityId;
        }

        if (canvasHoveredId === undefined) {
          const hits = raycaster.intersectObject(fixtures);
          const instanceId = hits.length > 0 ? hits[0].instanceId : undefined;
          const orgIdx = instanceId !== undefined ? (instanceIdToOrgIdxRef.current.get(instanceId) ?? -1) : -1;
          if (orgIdx !== -1) canvasHoveredId = rosterRef.current.find(r => r.idx === orgIdx)?.entity_id;
        }

        if (canvasHoveredId === undefined) {
          const rect = renderer.domElement.getBoundingClientRect();
          const mx = (mousePosNDC.x + 1) / 2 * rect.width;
          const my = (-mousePosNDC.y + 1) / 2 * rect.height;
          canvasHoveredId = findClosestEntityOnScreen(mx, my, rect, HOVER_PICK_PX);
        }

        if (canvasHoveredId !== prevHoveredFromCanvas) {
          prevHoveredFromCanvas = canvasHoveredId;
          onOrganismHoverRef.current?.(canvasHoveredId);
        }
      }

      // Follow-cam: keep camera behind and above the snapped entity
      const snapId = snapToEntityIdRef.current;
      if (snapId) {
        const targetPos = entityPositionsRef.current.get(snapId);
        if (targetPos) {
          camera.getWorldDirection(_fwd);
          _fwd.y = 0;
          if (_fwd.lengthSq() > 0.001) _fwd.normalize(); else _fwd.set(0, 0, -1);
          camera.position.set(
            targetPos.x - _fwd.x * 12,
            targetPos.y + 6,
            targetPos.z - _fwd.z * 12,
          );
        }
      }

      renderer.render(scene, camera);
    }
    animate();

    // ── WebGL context loss ───────────────────────────────────────────────────
    const handleContextLost = (e: Event) => {
      e.preventDefault(); // required to allow restoration
      cancelAnimationFrame(animId);
    };
    const handleContextRestored = () => {
      renderer.resetState();
      animate();
    };
    renderer.domElement.addEventListener('webglcontextlost',     handleContextLost);
    renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored);

    // ── Resize ───────────────────────────────────────────────────────────────
    const resizeObs = new ResizeObserver(() => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    });
    resizeObs.observe(mount);

    return () => {
      cancelAnimationFrame(animId);
      resizeObs.disconnect();
      renderer.domElement.removeEventListener('pointerdown',  onPointerDown);
      renderer.domElement.removeEventListener('pointermove',  onPointerMove);
      renderer.domElement.removeEventListener('pointerup',    onPointerUp);
      renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
      renderer.domElement.removeEventListener('wheel',        onWheel);
      renderer.domElement.removeEventListener('webglcontextlost',     handleContextLost);
      renderer.domElement.removeEventListener('webglcontextrestored', handleContextRestored);
      // Clean up all entity groups
      for (const groups of entityGroupsRef.current.values()) {
        for (const g of groups) {
          g.traverse(obj => {
            if (obj instanceof THREE.Mesh) {
              obj.geometry.dispose();
              (obj.material as THREE.Material).dispose();
            }
          });
        }
      }
      entityGroupsRef.current.clear();
      slotToEntityRef.current.clear();
      sceneRef.current = null;
      fixturesRef.current = null;
      fixGeo.dispose();
      fixMat.dispose();
      siteGeo.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  if (webglUnavailable) {
    return (
      <div className="broadcast-offline">
        <p>WEBGL UNAVAILABLE</p>
        <p className="broadcast-offline-hint">Your browser or GPU does not support WebGL</p>
      </div>
    );
  }

  if (broadcastOffline) {
    return (
      <div className="broadcast-offline">
        <p>SIM OFFLINE</p>
        <p className="broadcast-offline-hint">The simulation tunnel is currently down</p>
        <button onClick={() => { setBroadcastOffline(false); connectBroadcast(); }}>RETRY</button>
      </div>
    );
  }

  return (
    <div ref={mountRef} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', top: 8, right: 10, pointerEvents: 'none',
        fontFamily: '"Space Mono", monospace', fontSize: 11,
        color: 'rgba(150,210,255,0.6)', letterSpacing: '0.08em',
      }}>
        TICK <span ref={tickDisplayRef}>—</span>
        <span style={{ marginLeft: 10, opacity: 0.7 }}>TPS <span ref={tickRateRef} style={{ display: 'inline-block', width: '4.5ch', textAlign: 'right' }}>—</span></span>
      </div>
    </div>
  );
}
